import { type FC, Suspense, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { getRouteApi } from '@tanstack/react-router';
import { Loader2 } from 'lucide-react';

import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/new-ui/sheet';
import { parseObjectToSearchParams } from '@/utils/parseSearchParams';

import type { EstatesToolbarUrl } from '../constants/routes';
import type { EstatesFiltersSchemaType } from '../schemas';
import { EstateFilter } from './estate-filter';
import { hasActiveFilterValue } from './estate-filter-active-field';
import type { EstateFilterProps } from './estate-filter';

type EstateFilterDrawerProps = {
  url: EstatesToolbarUrl;
  creatorList?: EstateFilterProps['creatorList'];
  viewOptions?: EstateFilterProps['viewOptions'];
};

export const EstateFilterDrawer: FC<EstateFilterDrawerProps> = ({
  url,
  creatorList,
  viewOptions,
}) => {
  const route = getRouteApi(url);
  const { t } = useTranslation();
  const search = route.useSearch();
  const navigate = route.useNavigate();

  const drawerOpen = Boolean(search.enableFilters);

  const [filtersVersion, setFiltersVersion] = useState(0);

  const closeDrawer = useCallback(() => {
    navigate({
      search: (prev) => ({
        ...prev,
        enableFilters: false,
      }),
    });
  }, [navigate]);

  const { view, filters, filterOwn } = search;
  const parsedFilters = useMemo(
    () =>
      filters
        ? parseObjectToSearchParams<EstatesFiltersSchemaType>(filters as EstatesFiltersSchemaType)
        : undefined,
    [filters]
  );
  const hasFilters = useMemo(
    () => (parsedFilters ? hasActiveFilterValue(parsedFilters) : false),
    [parsedFilters]
  );

  const searchParams: EstateFilterProps['searchParams'] = useMemo(
    () => ({
      view,
      filters,
      filterOwn,
      enableFilters: true,
    }),
    [view, filters, filterOwn]
  );

  const handleUpdateSearchParams = (filters: EstatesFiltersSchemaType) => {
    navigate({
      search: (prev) => ({
        ...prev,
        filters,
      }),
    });
  };

  const handleClearFilters = () =>
    navigate({
      search: (prev) => ({
        ...prev,
        filters: undefined,
      }),
    }).then(() => {
      setFiltersVersion((v) => v + 1);
    });

  return (
    <Sheet
      open={drawerOpen}
      onOpenChange={(open) => {
        if (!open) closeDrawer();
      }}
    >
      <SheetContent className="w-full overflow-y-auto sm:max-w-md">
        <SheetHeader>
          <SheetTitle>{t('filterEstates', 'Filter Estates')}</SheetTitle>
          <SheetDescription>
            {t('filterEstatesDescription', 'Apply filters to narrow down your estate listings')}
          </SheetDescription>
        </SheetHeader>
        <div className="mt-6">
          {/* The filter loads the project list on first open; without its own boundary that
              suspended the whole page behind the sheet and showed the page spinner */}
          <Suspense
            fallback={
              <div className="flex justify-center py-12">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" aria-hidden />
              </div>
            }
          >
            <EstateFilter
              key={filtersVersion}
              hasFilters={hasFilters}
              onClear={handleClearFilters}
              creatorList={creatorList}
              searchParams={searchParams}
              viewOptions={viewOptions}
              onUpdateSearchParams={handleUpdateSearchParams}
            />
          </Suspense>
        </div>
      </SheetContent>
    </Sheet>
  );
};
